<template>
  <div class="gamification-detail-page">
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
      <p class="mt-2 text-muted">Carregando campanha...</p>
    </div>

    <div v-if="!loading && loadError" class="text-center py-5">
      <h4>Erro ao carregar campanha</h4>
      <p class="text-muted">{{ loadError }}</p>
      <b-button variant="primary" size="sm" @click="loadCampaign" class="mr-50">Tentar Novamente</b-button>
      <b-button variant="outline-secondary" size="sm" @click="$router.push({ name: 'gamification-campaigns' })">Voltar</b-button>
    </div>

    <div v-if="!loading && campaign">
      <div class="detail-header">
        <div class="header-left">
          <div class="mb-50">
            <b-button variant="flat-secondary" size="sm" @click="$router.push({ name: 'gamification-campaigns' })">
              <feather-icon icon="ArrowLeftIcon" size="14" class="mr-25" />
              Voltar
            </b-button>
          </div>
          <h2 class="detail-title">{{ campaign.nome }}</h2>
          <div class="detail-meta">
            <b-badge :variant="statusVariant(campaign.status)" pill>{{ statusLabel(campaign.status) }}</b-badge>
            <span class="meta-dates">
              <feather-icon icon="CalendarIcon" size="13" class="mr-25" />
              {{ formatDate(campaign.data_inicio) }} - {{ formatDate(campaign.data_fim) }}
            </span>
          </div>
        </div>
      </div>

      <b-tabs v-model="activeTab" content-class="mt-1" @input="onTabChange">
        <b-tab title="Overview">
          <div class="tab-content-inner">
            <b-card class="overview-card">
              <h6 class="section-title">Informações da Campanha</h6>
              <p v-if="campaign.descricao" class="text-muted" style="font-size: 0.85rem;">{{ campaign.descricao }}</p>
              <div class="overview-stats">
                <div class="ov-stat bg-light-primary">
                  <feather-icon icon="UsersIcon" size="16" class="text-primary" />
                  <div>
                    <span class="ov-stat-value">{{ overview.participants || 0 }}</span>
                    <span class="ov-stat-label text-muted">Participantes</span>
                  </div>
                </div>
                <div class="ov-stat bg-light-primary">
                  <feather-icon icon="StarIcon" size="16" class="text-primary" />
                  <div>
                    <span class="ov-stat-value">{{ overview.total_points || 0 }}</span>
                    <span class="ov-stat-label text-muted">Pontos Totais</span>
                  </div>
                </div>
                <div class="ov-stat bg-light-primary">
                  <feather-icon icon="AwardIcon" size="16" class="text-primary" />
                  <div>
                    <span class="ov-stat-value">{{ overview.badges_earned || 0 }}</span>
                    <span class="ov-stat-label text-muted">Medalhas Conquistadas</span>
                  </div>
                </div>
                <div class="ov-stat bg-light-primary">
                  <feather-icon icon="CheckCircleIcon" size="16" class="text-primary" />
                  <div>
                    <span class="ov-stat-value">{{ overview.missions_completed || 0 }}</span>
                    <span class="ov-stat-label text-muted">Missões Completas</span>
                  </div>
                </div>
              </div>
            </b-card>
          </div>
        </b-tab>

        <b-tab title="Regras">
          <div class="tab-content-inner">
            <div class="tab-toolbar">
              <h6 class="section-title mb-0">Regras de Pontuação</h6>
              <div class="toolbar-actions">
                <b-button variant="outline-primary" size="sm" @click="seedRules" class="mr-50">
                  <feather-icon icon="RefreshCwIcon" size="14" class="mr-25" />
                  Gerar Regras Padrão
                </b-button>
                <b-button variant="primary" size="sm" @click="openRuleModal()">
                  <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                  Nova Regra
                </b-button>
              </div>
            </div>
            <b-table
              :items="rules"
              :fields="ruleFields"
              :busy="rulesLoading"
              responsive
              hover
              show-empty
              empty-text="Nenhuma regra configurada"
              class="mb-0"
            >
              <template #table-busy>
                <div class="text-center py-3"><b-spinner small variant="primary" /></div>
              </template>
              <template #cell(is_active)="{ item }">
                <b-badge :variant="item.is_active ? 'light-success' : 'light-secondary'" pill>
                  {{ item.is_active ? 'Sim' : 'Não' }}
                </b-badge>
              </template>
              <template #cell(acoes)="{ item }">
                <b-button variant="flat-primary" size="sm" @click="openRuleModal(item)">
                  <feather-icon icon="EditIcon" size="14" />
                </b-button>
                <b-button variant="flat-danger" size="sm" @click="removeRule(item)">
                  <feather-icon icon="Trash2Icon" size="14" />
                </b-button>
              </template>
            </b-table>
          </div>
        </b-tab>

        <b-tab title="Missões">
          <div class="tab-content-inner">
            <div class="tab-toolbar">
              <h6 class="section-title mb-0">Missões</h6>
              <b-button variant="primary" size="sm" @click="openMissionModal()">
                <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                Nova Missão
              </b-button>
            </div>
            <b-table
              :items="missions"
              :fields="missionFields"
              :busy="missionsLoading"
              responsive
              hover
              show-empty
              empty-text="Nenhuma missão configurada"
              class="mb-0"
            >
              <template #table-busy>
                <div class="text-center py-3"><b-spinner small variant="primary" /></div>
              </template>
              <template #cell(is_active)="{ item }">
                <b-badge :variant="item.is_active ? 'light-success' : 'light-secondary'" pill>
                  {{ item.is_active ? 'Sim' : 'Não' }}
                </b-badge>
              </template>
              <template #cell(acoes)="{ item }">
                <b-button variant="flat-primary" size="sm" @click="openMissionModal(item)">
                  <feather-icon icon="EditIcon" size="14" />
                </b-button>
                <b-button variant="flat-danger" size="sm" @click="removeMission(item)">
                  <feather-icon icon="Trash2Icon" size="14" />
                </b-button>
              </template>
            </b-table>
          </div>
        </b-tab>

        <b-tab title="Medalhas">
          <div class="tab-content-inner">
            <div class="tab-toolbar">
              <h6 class="section-title mb-0">Medalhas</h6>
              <b-button variant="primary" size="sm" @click="openBadgeModal()">
                <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                Nova Medalha
              </b-button>
            </div>
            <div v-if="badgesLoading" class="text-center py-3"><b-spinner small variant="primary" /></div>
            <div v-else-if="badges.length === 0" class="text-center text-muted py-3">
              Nenhuma medalha configurada
            </div>
            <div v-else class="badges-grid">
              <b-card v-for="badge in badges" :key="badge.id" class="badge-card mb-0" body-class="badge-card-body">
                <div class="badge-emoji">{{ badge.icone || '🏅' }}</div>
                <div class="badge-info">
                  <h6 class="badge-name mb-25">{{ badge.nome }}</h6>
                  <p class="badge-desc mb-25 text-body">{{ badge.descricao }}</p>
                  <small class="text-muted">{{ badge.criterio }}</small>
                </div>
                <div class="badge-actions">
                  <b-button variant="flat-primary" size="sm" @click="openBadgeModal(badge)">
                    <feather-icon icon="EditIcon" size="14" />
                  </b-button>
                  <b-button variant="flat-danger" size="sm" @click="removeBadge(badge)">
                    <feather-icon icon="Trash2Icon" size="14" />
                  </b-button>
                </div>
              </b-card>
            </div>
          </div>
        </b-tab>

        <b-tab title="Ranking">
          <div class="tab-content-inner">
            <div class="tab-toolbar">
              <h6 class="section-title mb-0">Ranking</h6>
              <b-form-select v-model="rankingPeriod" size="sm" style="max-width: 160px;" @change="loadLeaderboard">
                <option value="total">Total</option>
                <option value="semana">Semana</option>
                <option value="mes">Mês</option>
              </b-form-select>
            </div>
            <b-table
              :items="leaderboard"
              :fields="leaderboardFields"
              :busy="leaderboardLoading"
              responsive
              hover
              show-empty
              empty-text="Nenhum dado de ranking disponível"
              class="mb-0"
            >
              <template #table-busy>
                <div class="text-center py-3"><b-spinner small variant="primary" /></div>
              </template>
              <template #cell(posicao)="{ index }">
                <span class="rank-position">
                  <template v-if="index === 0">🥇</template>
                  <template v-else-if="index === 1">🥈</template>
                  <template v-else-if="index === 2">🥉</template>
                  <template v-else>{{ index + 1 }}º</template>
                </span>
              </template>
            </b-table>
          </div>
        </b-tab>

        <b-tab title="Transações">
          <div class="tab-content-inner">
            <div class="tab-toolbar">
              <h6 class="section-title mb-0">Transações</h6>
              <b-button variant="primary" size="sm" @click="showManualPointsModal = true">
                <feather-icon icon="PlusCircleIcon" size="14" class="mr-25" />
                Adicionar Pontos Manuais
              </b-button>
            </div>
            <b-table
              :items="transactions"
              :fields="transactionFields"
              :busy="transactionsLoading"
              responsive
              hover
              show-empty
              empty-text="Nenhuma transação registrada"
              class="mb-0"
            >
              <template #table-busy>
                <div class="text-center py-3"><b-spinner small variant="primary" /></div>
              </template>
              <template #cell(created_at)="{ item }">
                {{ formatDateTime(item.created_at) }}
              </template>
              <template #cell(points)="{ item }">
                <span :class="item.points >= 0 ? 'text-success' : 'text-danger'">
                  {{ item.points >= 0 ? '+' : '' }}{{ item.points }}
                </span>
              </template>
            </b-table>
          </div>
        </b-tab>

        <b-tab title="Auditoria">
          <div class="tab-content-inner">
            <h6 class="section-title">Log de Auditoria</h6>
            <b-table
              :items="auditLog"
              :fields="auditFields"
              :busy="auditLoading"
              responsive
              hover
              show-empty
              empty-text="Nenhum registro de auditoria"
              class="mb-0"
            >
              <template #table-busy>
                <div class="text-center py-3"><b-spinner small variant="primary" /></div>
              </template>
              <template #cell(created_at)="{ item }">
                {{ formatDateTime(item.created_at) }}
              </template>
            </b-table>
          </div>
        </b-tab>
      </b-tabs>
    </div>

    <b-modal
      v-model="showRuleModal"
      :title="editingRule ? 'Editar Regra' : 'Nova Regra'"
      ok-title="Salvar"
      cancel-title="Cancelar"
      @ok="saveRule"
      :ok-disabled="!ruleForm.event_type || !ruleForm.event_label"
      centered
      no-close-on-backdrop
    >
      <b-row>
        <b-col md="6">
          <b-form-group label="Evento *" label-for="rule-evento">
            <b-form-input id="rule-evento" v-model="ruleForm.event_type" placeholder="Ex: ORDER_CREATED" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Label *" label-for="rule-label">
            <b-form-input id="rule-label" v-model="ruleForm.event_label" placeholder="Ex: Novo Pedido" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="4">
          <b-form-group label="Pontos" label-for="rule-pontos">
            <b-form-input id="rule-pontos" v-model.number="ruleForm.points" type="number" />
          </b-form-group>
        </b-col>
        <b-col md="8">
          <b-form-group label="Descrição" label-for="rule-desc">
            <b-form-input id="rule-desc" v-model="ruleForm.description" placeholder="Descrição da regra" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group>
        <b-form-select v-model="ruleForm.is_active">
          <option :value="true">Ativo</option>
          <option :value="false">Inativo</option>
        </b-form-select>
      </b-form-group>
    </b-modal>

    <b-modal
      v-model="showMissionModal"
      :title="editingMission ? 'Editar Missão' : 'Nova Missão'"
      ok-title="Salvar"
      cancel-title="Cancelar"
      @ok="saveMission"
      :ok-disabled="!missionForm.nome"
      centered
      no-close-on-backdrop
    >
      <b-row>
        <b-col md="6">
          <b-form-group label="Nome *" label-for="miss-nome">
            <b-form-input id="miss-nome" v-model="missionForm.nome" placeholder="Nome da missão" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Evento" label-for="miss-evento">
            <b-form-input id="miss-evento" v-model="missionForm.event_type" placeholder="Ex: ORDER_CREATED" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="4">
          <b-form-group label="Meta" label-for="miss-meta">
            <b-form-input id="miss-meta" v-model.number="missionForm.quantidade_alvo" type="number" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Bônus Pts" label-for="miss-bonus">
            <b-form-input id="miss-bonus" v-model.number="missionForm.bonus_points" type="number" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Prazo (dias)" label-for="miss-prazo">
            <b-form-input id="miss-prazo" v-model.number="missionForm.prazo_dias" type="number" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group>
        <b-form-select v-model="missionForm.is_active">
          <option :value="true">Ativo</option>
          <option :value="false">Inativo</option>
        </b-form-select>
      </b-form-group>
    </b-modal>

    <b-modal
      v-model="showBadgeModal"
      :title="editingBadge ? 'Editar Medalha' : 'Nova Medalha'"
      ok-title="Salvar"
      cancel-title="Cancelar"
      @ok="saveBadge"
      :ok-disabled="!badgeForm.nome"
      centered
      no-close-on-backdrop
    >
      <b-row>
        <b-col md="2">
          <b-form-group label="Ícone" label-for="badge-icone">
            <b-form-input id="badge-icone" v-model="badgeForm.icone" placeholder="🏅" />
          </b-form-group>
        </b-col>
        <b-col md="10">
          <b-form-group label="Nome *" label-for="badge-nome">
            <b-form-input id="badge-nome" v-model="badgeForm.nome" placeholder="Nome da medalha" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group label="Descrição" label-for="badge-desc">
        <b-form-textarea id="badge-desc" v-model="badgeForm.descricao" rows="2" placeholder="Descrição da medalha" />
      </b-form-group>
      <b-form-group label="Critério" label-for="badge-criterio">
        <b-form-input id="badge-criterio" v-model="badgeForm.criterio" placeholder="Ex: Realizar 50 pedidos" />
      </b-form-group>
    </b-modal>

    <b-modal
      v-model="showManualPointsModal"
      title="Adicionar Pontos Manuais"
      ok-title="Adicionar"
      cancel-title="Cancelar"
      @ok="addManualPoints"
      :ok-disabled="!manualForm.representante_id || !manualForm.pontos"
      centered
      no-close-on-backdrop
    >
      <b-form-group label="Representante *" label-for="mp-rep">
        <b-form-input id="mp-rep" v-model="manualForm.representante_id" placeholder="ID do representante" />
      </b-form-group>
      <b-form-group label="Pontos *" label-for="mp-pontos">
        <b-form-input id="mp-pontos" v-model.number="manualForm.pontos" type="number" />
      </b-form-group>
      <b-form-group label="Descrição" label-for="mp-desc">
        <b-form-textarea id="mp-desc" v-model="manualForm.descricao" rows="2" placeholder="Motivo da pontuação manual" />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import gamificationClient from '@/api/CRM/gamification.client';
import {
  BCard, BTable, BButton, BBadge, BModal, BFormInput, BFormGroup,
  BFormSelect, BFormTextarea, BSpinner, BRow, BCol, BTabs, BTab,
} from 'bootstrap-vue';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'GamificationCampaignDetail',
  components: {
    BCard, BTable, BButton, BBadge, BModal, BFormInput, BFormGroup,
    BFormSelect, BFormTextarea, BSpinner, BRow, BCol, BTabs, BTab,
  },
  data() {
    return {
      loading: true,
      loadError: null,
      campaign: null,
      activeTab: 0,
      overview: {},
      rules: [],
      rulesLoading: false,
      missions: [],
      missionsLoading: false,
      badges: [],
      badgesLoading: false,
      leaderboard: [],
      leaderboardLoading: false,
      rankingPeriod: 'total',
      transactions: [],
      transactionsLoading: false,
      auditLog: [],
      auditLoading: false,
      showRuleModal: false,
      editingRule: null,
      ruleForm: { event_type: '', event_label: '', points: 10, description: '', is_active: true },
      showMissionModal: false,
      editingMission: null,
      missionForm: { nome: '', event_type: '', quantidade_alvo: 1, bonus_points: 50, prazo_dias: 30, is_active: true },
      showBadgeModal: false,
      editingBadge: null,
      badgeForm: { icone: '🏅', nome: '', descricao: '', criterio: '' },
      showManualPointsModal: false,
      manualForm: { representante_id: '', pontos: 0, descricao: '' },
      ruleFields: [
        { key: 'event_type', label: 'Evento' },
        { key: 'event_label', label: 'Label' },
        { key: 'points', label: 'Pontos', class: 'text-center' },
        { key: 'description', label: 'Descrição' },
        { key: 'is_active', label: 'Ativo', class: 'text-center' },
        { key: 'acoes', label: 'Ações', class: 'text-center', thStyle: 'width: 100px' },
      ],
      missionFields: [
        { key: 'nome', label: 'Nome' },
        { key: 'event_type', label: 'Evento' },
        { key: 'quantidade_alvo', label: 'Meta', class: 'text-center' },
        { key: 'bonus_points', label: 'Bônus Pts', class: 'text-center' },
        { key: 'prazo_dias', label: 'Prazo', class: 'text-center' },
        { key: 'is_active', label: 'Ativo', class: 'text-center' },
        { key: 'acoes', label: 'Ações', class: 'text-center', thStyle: 'width: 100px' },
      ],
      leaderboardFields: [
        { key: 'posicao', label: '#', class: 'text-center', thStyle: 'width: 60px' },
        { key: 'representantenome', label: 'Nome' },
        { key: 'total_points', label: 'Pontos Total', class: 'text-center' },
        { key: 'week_points', label: 'Pontos Semana', class: 'text-center' },
        { key: 'month_points', label: 'Pontos Mês', class: 'text-center' },
      ],
      transactionFields: [
        { key: 'created_at', label: 'Data' },
        { key: 'representantenome', label: 'Representante' },
        { key: 'event_type', label: 'Evento' },
        { key: 'points', label: 'Pontos', class: 'text-center' },
        { key: 'description', label: 'Descrição' },
      ],
      auditFields: [
        { key: 'created_at', label: 'Data' },
        { key: 'user_name', label: 'Usuário' },
        { key: 'action', label: 'Ação' },
        { key: 'details', label: 'Detalhes' },
      ],
    };
  },
  created() {
    this.loadCampaign();
  },
  computed: {
    campaignId() {
      return this.$route.params.id;
    },
  },
  methods: {
    async loadCampaign() {
      this.loading = true;
      this.loadError = null;
      try {
        const id = this.$route.params.id;
        if (!id) { this.loadError = 'ID da campanha não informado'; return; }
        const result = await gamificationClient.getCampaign(id);
        const data = result.data || result;
        if (!data || !data.id) { this.loadError = 'Campanha não encontrada'; return; }
        this.campaign = data;
        this.loadOverview();
      } catch (error) {
        this.loadError = error.response?.data?.message || error.message || 'Erro desconhecido';
      } finally {
        this.loading = false;
      }
    },
    onTabChange(tabIndex) {
      const loaders = {
        0: 'loadOverview',
        1: 'loadRules',
        2: 'loadMissions',
        3: 'loadBadges',
        4: 'loadLeaderboard',
        5: 'loadTransactions',
        6: 'loadAudit',
      };
      if (loaders[tabIndex]) this[loaders[tabIndex]]();
    },
    async loadOverview() {
      try {
        const result = await gamificationClient.getCampaignOverview(this.$route.params.id);
        const raw = result.data || result || {};
        this.overview = {
          participants: raw.total_participants || raw.participants || 0,
          total_points: raw.total_points_distributed || raw.total_points || 0,
          badges_earned: raw.total_badges_earned || raw.badges_earned || 0,
          missions_completed: raw.total_missions_completed || raw.missions_completed || 0,
          total_transactions: raw.total_transactions || 0,
        };
      } catch (e) { console.error('Erro overview:', e); }
    },
    async loadRules() {
      this.rulesLoading = true;
      try {
        const result = await gamificationClient.listRules(this.$route.params.id);
        this.rules = result.data || result || [];
      } catch (e) { console.error('Erro rules:', e); }
      finally { this.rulesLoading = false; }
    },
    async loadMissions() {
      this.missionsLoading = true;
      try {
        const result = await gamificationClient.listMissions(this.$route.params.id);
        this.missions = result.data || result || [];
      } catch (e) { console.error('Erro missions:', e); }
      finally { this.missionsLoading = false; }
    },
    async loadBadges() {
      this.badgesLoading = true;
      try {
        const result = await gamificationClient.listBadges(this.$route.params.id);
        this.badges = result.data || result || [];
      } catch (e) { console.error('Erro badges:', e); }
      finally { this.badgesLoading = false; }
    },
    async loadLeaderboard() {
      this.leaderboardLoading = true;
      try {
        const result = await gamificationClient.getLeaderboard(this.$route.params.id, { period: this.rankingPeriod });
        this.leaderboard = result.data || result || [];
      } catch (e) { console.error('Erro leaderboard:', e); }
      finally { this.leaderboardLoading = false; }
    },
    async loadTransactions() {
      this.transactionsLoading = true;
      try {
        const result = await gamificationClient.getTransactions(this.$route.params.id);
        const payload = result.data || result;
        this.transactions = payload.rows || payload.items || (Array.isArray(payload) ? payload : []);
      } catch (e) { console.error('Erro transactions:', e); }
      finally { this.transactionsLoading = false; }
    },
    async loadAudit() {
      this.auditLoading = true;
      try {
        const result = await gamificationClient.getAuditLog(this.$route.params.id);
        const payload = result.data || result;
        this.auditLog = Array.isArray(payload) ? payload : (payload.rows || payload.items || []);
      } catch (e) { console.error('Erro audit:', e); }
      finally { this.auditLoading = false; }
    },
    async seedRules() {
      try {
        await gamificationClient.seedDefaultRules(this.$route.params.id);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Regras padrão geradas com sucesso',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
        this.loadRules();
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao gerar regras padrão',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    openRuleModal(item) {
      if (item) {
        this.editingRule = item;
        this.ruleForm = { event_type: item.event_type, event_label: item.event_label, points: item.points, description: item.description || '', is_active: !!item.is_active };
      } else {
        this.editingRule = null;
        this.ruleForm = { event_type: '', event_label: '', points: 10, description: '', is_active: true };
      }
      this.showRuleModal = true;
    },
    async saveRule(e) {
      e.preventDefault();
      try {
        if (this.editingRule) {
          await gamificationClient.updateRule(this.editingRule.id, this.ruleForm);
        } else {
          await gamificationClient.createRule(this.$route.params.id, this.ruleForm);
        }
        this.showRuleModal = false;
        this.loadRules();
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Regra salva com sucesso',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao salvar regra',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    async removeRule(item) {
      if (!confirm('Excluir esta regra?')) return;
      try {
        await gamificationClient.deleteRule(item.id, this.$route.params.id);
        this.rules = this.rules.filter(r => r.id !== item.id);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Regra excluída com sucesso',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao excluir regra',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    openMissionModal(item) {
      if (item) {
        this.editingMission = item;
        this.missionForm = { nome: item.nome, event_type: item.event_type || '', quantidade_alvo: item.quantidade_alvo, bonus_points: item.bonus_points, prazo_dias: item.prazo_dias || 30, is_active: !!item.is_active };
      } else {
        this.editingMission = null;
        this.missionForm = { nome: '', event_type: '', quantidade_alvo: 1, bonus_points: 50, prazo_dias: 30, is_active: true };
      }
      this.showMissionModal = true;
    },
    async saveMission(e) {
      e.preventDefault();
      try {
        if (this.editingMission) {
          await gamificationClient.updateMission(this.editingMission.id, this.missionForm);
        } else {
          await gamificationClient.createMission(this.$route.params.id, this.missionForm);
        }
        this.showMissionModal = false;
        this.loadMissions();
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Missão salva com sucesso',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao salvar missão',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    async removeMission(item) {
      if (!confirm('Excluir esta missão?')) return;
      try {
        await gamificationClient.deleteMission(item.id, this.$route.params.id);
        this.missions = this.missions.filter(m => m.id !== item.id);
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao excluir missão',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    openBadgeModal(item) {
      if (item) {
        this.editingBadge = item;
        this.badgeForm = { icone: item.icone || '🏅', nome: item.nome, descricao: item.descricao || '', criterio: item.criterio || '' };
      } else {
        this.editingBadge = null;
        this.badgeForm = { icone: '🏅', nome: '', descricao: '', criterio: '' };
      }
      this.showBadgeModal = true;
    },
    async saveBadge(e) {
      e.preventDefault();
      try {
        if (this.editingBadge) {
          await gamificationClient.updateBadge(this.editingBadge.id, this.badgeForm);
        } else {
          await gamificationClient.createBadge(this.$route.params.id, this.badgeForm);
        }
        this.showBadgeModal = false;
        this.loadBadges();
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Medalha salva com sucesso',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao salvar medalha',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    async removeBadge(item) {
      if (!confirm('Excluir esta medalha?')) return;
      try {
        await gamificationClient.deleteBadge(item.id, this.$route.params.id);
        this.badges = this.badges.filter(b => b.id !== item.id);
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao excluir medalha',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    async addManualPoints(e) {
      e.preventDefault();
      try {
        await gamificationClient.addManualPoints(this.$route.params.id, this.manualForm);
        this.showManualPointsModal = false;
        this.manualForm = { representante_id: '', pontos: 0, descricao: '' };
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Pontos adicionados com sucesso',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        });
        this.loadTransactions();
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao adicionar pontos',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    statusVariant(s) {
      const map = {
        ativa: 'light-success',
        rascunho: 'light-secondary',
        inativa: 'light-warning',
        arquivada: 'light-dark',
      };
      return map[s] || 'light-secondary';
    },
    statusLabel(s) {
      const map = { ativa: 'Ativa', rascunho: 'Rascunho', inativa: 'Inativa', arquivada: 'Arquivada' };
      return map[s] || s;
    },
    formatDate(d) {
      if (!d) return '-';
      return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    formatDateTime(d) {
      if (!d) return '-';
      return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>

<style scoped>
.gamification-detail-page {
  padding: 0.75rem;
  position: relative;
}

.detail-header {
  margin-bottom: 1.25rem;
}

.detail-title {
  font-size: 1.35rem;
  font-weight: 600;
  margin: 0 0 0.35rem;
}

.detail-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.meta-dates {
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
}

.tab-content-inner {
  padding: 1rem 0;
}

.tab-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.toolbar-actions {
  display: flex;
  gap: 0.25rem;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.overview-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.ov-stat {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem;
  border-radius: 8px;
}

.ov-stat-value {
  display: block;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
}

.ov-stat-label {
  display: block;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.badges-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.875rem;
}

.badge-card-body {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
}

.badge-emoji {
  font-size: 2rem;
  flex-shrink: 0;
  line-height: 1;
}

.badge-info {
  flex: 1;
  min-width: 0;
}

.badge-name {
  font-size: 0.875rem;
  font-weight: 600;
}

.badge-desc {
  font-size: 0.78rem;
  line-height: 1.3;
}

.badge-actions {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  flex-shrink: 0;
}

.rank-position {
  font-size: 1rem;
  font-weight: 600;
}

@media (max-width: 768px) {
  .overview-stats {
    grid-template-columns: 1fr 1fr;
  }
  .badges-grid {
    grid-template-columns: 1fr;
  }
  .tab-toolbar {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
